import { useMemo, useState } from 'react';

import type { CapturedRequest } from '../lib/api.js';
import { toCurl } from '../lib/curl.js';
import { formatTimestamp, methodTone, statusTone } from '../lib/format.js';
import { BodyViewer } from './BodyViewer.js';
import { CopyButton } from './CopyButton.js';

export interface RequestDetailProps {
  request: CapturedRequest;
  /** The endpoint's public capture URL, needed to rebuild a runnable curl. */
  endpointUrl: string;
}

type Tab = 'headers' | 'body' | 'query';

const TABS: { id: Tab; label: string }[] = [
  { id: 'headers', label: 'Cabeceras' },
  { id: 'body', label: 'Cuerpo' },
  { id: 'query', label: 'Query' },
];

export function RequestDetail({ request, endpointUrl }: RequestDetailProps) {
  const [tab, setTab] = useState<Tab>('body');
  const curl = useMemo(() => toCurl(request, { endpointUrl }), [request, endpointUrl]);

  const headers = Object.entries(request.headers ?? {});
  const query = Object.entries(request.query ?? {});

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="shrink-0 border-b border-slate-800 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded px-2 py-0.5 font-mono text-xs font-semibold ${methodTone(request.method)}`}
          >
            {request.method}
          </span>
          <span className="min-w-0 flex-1 break-all font-mono text-sm text-slate-100">
            {request.path}
          </span>
          <span
            className={`rounded px-2 py-0.5 font-mono text-xs ring-1 ${statusTone(request.statusReturned)}`}
          >
            {request.statusReturned}
          </span>
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-400 sm:grid-cols-4">
          <Fact label="Recibido" value={formatTimestamp(request.createdAt)} />
          <Fact label="Latencia" value={`${request.durationMs} ms`} />
          <Fact label="Origen" value={request.ip ?? '—'} />
          <Fact label="Agente" value={request.userAgent ?? '—'} />
        </dl>

        <div className="mt-3">
          <CopyButton
            value={curl}
            label="Copiar como curl"
            title="Copia un comando que reproduce este request"
            className="rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-200 transition hover:bg-sky-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          />
        </div>
      </header>

      <div
        role="tablist"
        aria-label="Detalle del request"
        className="flex shrink-0 border-b border-slate-800"
      >
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400 ${
              tab === id
                ? 'border-b-2 border-sky-400 text-slate-100'
                : 'border-b-2 border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            {label}
            {id === 'headers' && <Count value={headers.length} />}
            {id === 'query' && <Count value={query.length} />}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
        {tab === 'headers' && (
          <Panel id="headers">
            <PairTable pairs={headers} empty="Este request no traía cabeceras." />
          </Panel>
        )}

        {tab === 'body' && (
          <Panel id="body">
            <BodyViewer request={request} />
          </Panel>
        )}

        {tab === 'query' && (
          <Panel id="query">
            <PairTable pairs={query} empty="La URL no llevaba parámetros." />
          </Panel>
        )}
      </div>
    </div>
  );
}

function Panel({ id, children }: { id: Tab; children: React.ReactNode }) {
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} tabIndex={0}>
      {children}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="truncate text-slate-300" title={value}>
        {value}
      </dd>
    </div>
  );
}

function Count({ value }: { value: number }) {
  if (value === 0) return null;
  return (
    <span className="ml-1.5 rounded bg-slate-800 px-1.5 text-[11px] text-slate-400">{value}</span>
  );
}

function PairTable({ pairs, empty }: { pairs: [string, unknown][]; empty: string }) {
  if (pairs.length === 0) return <p className="text-sm text-slate-500">{empty}</p>;

  return (
    <div className="overflow-x-auto rounded-md bg-slate-950/60">
      <table className="w-full text-left font-mono text-xs">
        <tbody>
          {pairs.map(([key, value]) => (
            <tr key={key} className="border-b border-slate-800/60 last:border-0">
              <td className="w-1/3 px-3 py-1.5 align-top text-sky-300">{key}</td>
              <td className="whitespace-pre-wrap break-all px-3 py-1.5 text-slate-200">
                {/* An array is a repeated header or query key; show every value. */}
                {Array.isArray(value) ? value.join('\n') : String(value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
